import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  FileText, RefreshCw, Eye, Search, Calendar, User, 
  Activity, AlertTriangle, CheckCircle, XCircle, Clock,
  ChevronLeft, ChevronRight, Filter, Download, BarChart3,
  Trash2, Edit, Plus, CreditCard, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

// API endpoints
const BACKEND_API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_API_BASE}/api`;

// Helper function to format date/time
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Format date only
const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Get action badge color
const getActionBadge = (action) => {
  const actionConfig = {
    create: { color: 'bg-emerald-500/20 text-emerald-400', icon: Plus, label: 'Created' },
    delete: { color: 'bg-red-500/20 text-red-400', icon: Trash2, label: 'Deleted' },
    cancel: { color: 'bg-orange-500/20 text-orange-400', icon: XCircle, label: 'Cancelled' },
    auto_cancel: { color: 'bg-amber-500/20 text-amber-400', icon: Clock, label: 'Auto Cancelled' },
    update: { color: 'bg-blue-500/20 text-blue-400', icon: Edit, label: 'Updated' },
    payment: { color: 'bg-purple-500/20 text-purple-400', icon: CreditCard, label: 'Payment' },
    reschedule: { color: 'bg-cyan-500/20 text-cyan-400', icon: RotateCcw, label: 'Rescheduled' }
  };
  
  return actionConfig[action] || { color: 'bg-slate-500/20 text-slate-400', icon: Activity, label: action };
};

// Get entity type badge
const getEntityBadge = (entityType) => {
  const config = {
    booking: 'bg-blue-500/20 text-blue-400',
    user: 'bg-green-500/20 text-green-400',
    court: 'bg-purple-500/20 text-purple-400',
    block_off: 'bg-amber-500/20 text-amber-400',
    pos_item: 'bg-pink-500/20 text-pink-400',
    pos_order: 'bg-orange-500/20 text-orange-400',
    pricing: 'bg-cyan-500/20 text-cyan-400'
  };
  
  return config[entityType] || 'bg-slate-500/20 text-slate-400';
};

const AdminLogsPage = () => {
  const { token, admin } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Modal state
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Stats modal
  const [statsModalOpen, setStatsModalOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (entityTypeFilter) params.append('entity_type', entityTypeFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (adminFilter) params.append('admin_id', adminFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API}/admin/audit-logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setAccessDenied(false);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [token, page, entityTypeFilter, actionFilter, adminFilter, dateFrom, dateTo, searchQuery]);

  const fetchStats = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/admin/audit-logs/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchAdmins = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/admin/admins-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.admins || []);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchAdmins();
  }, [token]);

  const handleSearch = () => {
    setPage(1);
    fetchLogs();
  };

  const handleReset = () => {
    setSearchQuery('');
    setEntityTypeFilter('');
    setActionFilter('');
    setAdminFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const viewLogDetails = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  const exportLogs = async () => {
    try {
      // Export current filtered results as CSV
      const csvContent = [
        ['Log ID', 'Timestamp', 'Admin', 'Action', 'Entity Type', 'Entity ID', 'Customer', 'Details'].join(','),
        ...logs.map(log => [
          log.log_id,
          formatDateTime(log.created_at),
          log.admin_name || log.admin_email,
          log.action,
          log.entity_type,
          log.entity_id,
          log.details?.customer_name || '-',
          JSON.stringify(log.details || {}).replace(/,/g, ';')
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Logs exported successfully');
    } catch (err) {
      toast.error('Failed to export logs');
    }
  };

  // Access denied view
  if (accessDenied) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <AlertTriangle className="h-16 w-16 text-amber-400 mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Access Denied</h2>
          <p className={`${theme.textMuted} text-center max-w-md`}>
            Only the primary super admin (admin@camsurpickleball.com) can access the audit logs.
            This feature is restricted for security and compliance purposes.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Audit Logs</h1>
            <p className={theme.textMuted}>
              Track all admin actions and booking changes for accountability
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setStatsModalOpen(true)}
              className={`${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Statistics
            </Button>
            <Button
              variant="outline"
              onClick={exportLogs}
              className={`${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={() => fetchLogs()}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className={theme.card}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <FileText className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textMuted}`}>Total Logs</p>
                    <p className={`text-xl font-bold ${theme.text}`}>{stats.total_logs?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={theme.card}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-500/20">
                    <Activity className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textMuted}`}>Last 24 Hours</p>
                    <p className={`text-xl font-bold ${theme.text}`}>{stats.recent_activity_24h?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={theme.card}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Plus className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textMuted}`}>Bookings Created</p>
                    <p className={`text-xl font-bold ${theme.text}`}>{stats.by_action?.create?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={theme.card}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <XCircle className="h-5 w-5 text-red-400" />
                  </div>
                  <div>
                    <p className={`text-sm ${theme.textMuted}`}>Cancelled/Deleted</p>
                    <p className={`text-xl font-bold ${theme.text}`}>
                      {((stats.by_action?.cancel || 0) + (stats.by_action?.delete || 0) + (stats.by_action?.auto_cancel || 0)).toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className={theme.card}>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <Label className={theme.textMuted}>Search</Label>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${theme.textMuted}`} />
                  <Input
                    placeholder="Search by customer, booking ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className={`pl-10 ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>
              <div>
                <Label className={theme.textMuted}>Action Type</Label>
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                  className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">All Actions</option>
                  <option value="create">Created</option>
                  <option value="update">Updated</option>
                  <option value="delete">Deleted</option>
                  <option value="cancel">Cancelled</option>
                  <option value="auto_cancel">Auto Cancelled</option>
                  <option value="payment">Payment</option>
                  <option value="reschedule">Rescheduled</option>
                </select>
              </div>
              <div>
                <Label className={theme.textMuted}>Entity Type</Label>
                <select
                  value={entityTypeFilter}
                  onChange={(e) => setEntityTypeFilter(e.target.value)}
                  className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">All Entities</option>
                  <option value="booking">Booking</option>
                  <option value="user">User</option>
                  <option value="court">Court</option>
                  <option value="block_off">Block Off</option>
                  <option value="pos_order">POS Order</option>
                </select>
              </div>
              <div>
                <Label className={theme.textMuted}>Admin User</Label>
                <select
                  value={adminFilter}
                  onChange={(e) => setAdminFilter(e.target.value)}
                  className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">All Admins</option>
                  <option value="SYSTEM">System (Automatic)</option>
                  {admins.map(a => (
                    <option key={a.admin_id} value={a.admin_id}>{a.name} ({a.role})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label className={theme.textMuted}>Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={`${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div>
                <Label className={theme.textMuted}>Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={`${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}
                />
              </div>
              <div className="flex items-end gap-2 lg:col-span-2">
                <Button onClick={handleSearch} className="bg-emerald-600 hover:bg-emerald-700">
                  <Filter className="h-4 w-4 mr-2" />
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={handleReset} className={isDarkMode ? 'border-slate-600' : ''}>
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className={theme.card}>
          <CardHeader>
            <CardTitle className={`flex items-center justify-between ${theme.text}`}>
              <span>Activity Log ({total.toLocaleString()} records)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-emerald-500" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText className={`h-12 w-12 ${theme.textMuted} mb-4`} />
                <p className={theme.textMuted}>No audit logs found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Timestamp</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Admin</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Action</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Entity</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Booking ID</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Customer</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Date</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Time</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Court</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Amount</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Status</th>
                        <th className={`text-left py-3 px-4 font-medium ${theme.textMuted}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => {
                        const actionConfig = getActionBadge(log.action);
                        const ActionIcon = actionConfig.icon;
                        
                        return (
                          <tr 
                            key={log.log_id} 
                            className={`border-b ${isDarkMode ? 'border-slate-700/50 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'}`}
                          >
                            <td className={`py-3 px-4 ${theme.text}`}>
                              <div className="text-sm">
                                {formatDateTime(log.created_at)}
                              </div>
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-slate-400" />
                                <div>
                                  <div className="text-sm font-medium">{log.admin_name || 'System'}</div>
                                  <div className={`text-xs ${theme.textMuted}`}>{log.admin_role}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-col gap-1">
                                <Badge className={`${actionConfig.color} flex items-center gap-1 w-fit`}>
                                  <ActionIcon className="h-3 w-3" />
                                  {actionConfig.label}
                                </Badge>
                                <div className={`text-xs ${theme.textMuted}`}>
                                  by {log.admin_name || log.admin_email || 'System'}
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={getEntityBadge(log.entity_type)}>
                                {log.entity_type}
                              </Badge>
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              <code className="text-xs bg-slate-700/50 px-2 py-1 rounded">
                                {log.entity_id?.substring(0, 16)}...
                              </code>
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              {log.details?.customer_name || '-'}
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              {log.details?.date || log.details?.previous_date || '-'}
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              {log.details?.start_time || log.details?.previous_time || '-'}
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              {log.details?.court_id || log.details?.previous_court || '-'}
                            </td>
                            <td className={`py-3 px-4 ${theme.text}`}>
                              {log.details?.total_amount ? `₱${log.details.total_amount.toLocaleString()}` : '-'}
                            </td>
                            <td className="py-3 px-4">
                              {log.details?.previous_status && log.details?.new_status ? (
                                <div className="flex items-center gap-1 text-xs">
                                  <Badge className="bg-slate-500/20 text-slate-400">
                                    {log.details.previous_status}
                                  </Badge>
                                  <span className={theme.textMuted}>→</span>
                                  <Badge className={log.details.new_status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                                    {log.details.new_status}
                                  </Badge>
                                </div>
                              ) : (
                                <Badge className={
                                  log.details?.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                                  log.details?.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                  'bg-slate-500/20 text-slate-400'
                                }>
                                  {log.details?.status || log.details?.previous_status || '-'}
                                </Badge>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => viewLogDetails(log)}
                                className="text-emerald-400 hover:text-emerald-300"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700/50">
                  <p className={theme.textMuted}>
                    Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} records
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className={isDarkMode ? 'border-slate-600' : ''}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className={theme.text}>
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className={isDarkMode ? 'border-slate-600' : ''}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Log Detail Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className={`${theme.card} max-w-3xl max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>Audit Log Details</DialogTitle>
              <DialogDescription className={theme.textMuted}>
                Complete information about this action
              </DialogDescription>
            </DialogHeader>
            
            {selectedLog && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.textMuted}>Log ID</Label>
                    <p className={`font-mono text-sm ${theme.text}`}>{selectedLog.log_id}</p>
                  </div>
                  <div>
                    <Label className={theme.textMuted}>Timestamp</Label>
                    <p className={theme.text}>{formatDateTime(selectedLog.created_at)}</p>
                  </div>
                  <div>
                    <Label className={theme.textMuted}>Admin</Label>
                    <p className={theme.text}>{selectedLog.admin_name} ({selectedLog.admin_email})</p>
                  </div>
                  <div>
                    <Label className={theme.textMuted}>Role</Label>
                    <Badge className="bg-purple-500/20 text-purple-400">{selectedLog.admin_role}</Badge>
                  </div>
                  <div>
                    <Label className={theme.textMuted}>Action</Label>
                    <Badge className={getActionBadge(selectedLog.action).color}>
                      {getActionBadge(selectedLog.action).label}
                    </Badge>
                  </div>
                  <div>
                    <Label className={theme.textMuted}>Entity Type</Label>
                    <Badge className={getEntityBadge(selectedLog.entity_type)}>
                      {selectedLog.entity_type}
                    </Badge>
                  </div>
                </div>

                {/* Entity ID */}
                <div>
                  <Label className={theme.textMuted}>Entity ID</Label>
                  <code className={`block p-2 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'} ${theme.text} text-sm`}>
                    {selectedLog.entity_id}
                  </code>
                </div>

                {/* Action Details */}
                <div>
                  <Label className={theme.textMuted}>Action Details</Label>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <dl className="grid grid-cols-2 gap-3">
                      {Object.entries(selectedLog.details || {}).map(([key, value]) => (
                        <div key={key}>
                          <dt className={`text-xs ${theme.textMuted} capitalize`}>{key.replace(/_/g, ' ')}</dt>
                          <dd className={`text-sm ${theme.text}`}>
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                </div>

                {/* Before/After Data */}
                {(selectedLog.before_data || selectedLog.after_data) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLog.before_data && (
                      <div>
                        <Label className={theme.textMuted}>Before (Original Data)</Label>
                        <pre className={`p-3 rounded text-xs overflow-auto max-h-48 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'} ${theme.text}`}>
                          {JSON.stringify(selectedLog.before_data, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.after_data && (
                      <div>
                        <Label className={theme.textMuted}>After (New Data)</Label>
                        <pre className={`p-3 rounded text-xs overflow-auto max-h-48 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'} ${theme.text}`}>
                          {JSON.stringify(selectedLog.after_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Statistics Modal */}
        <Dialog open={statsModalOpen} onOpenChange={setStatsModalOpen}>
          <DialogContent className={`${theme.card} max-w-2xl`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>Audit Log Statistics</DialogTitle>
              <DialogDescription className={theme.textMuted}>
                Overview of admin activity and actions
              </DialogDescription>
            </DialogHeader>
            
            {stats && (
              <div className="space-y-6">
                {/* By Action */}
                <div>
                  <Label className={theme.textMuted}>Actions Breakdown</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {Object.entries(stats.by_action || {}).map(([action, count]) => {
                      const config = getActionBadge(action);
                      return (
                        <div key={action} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                          <Badge className={config.color}>{config.label}</Badge>
                          <span className={`font-bold ${theme.text}`}>{count.toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* By Admin */}
                <div>
                  <Label className={theme.textMuted}>Activity by Admin</Label>
                  <div className="space-y-2 mt-2">
                    {(stats.by_admin || []).map((admin, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-2 rounded ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                        <div>
                          <p className={`font-medium ${theme.text}`}>{admin.admin_name || 'System'}</p>
                          <p className={`text-xs ${theme.textMuted}`}>{admin.admin_email}</p>
                        </div>
                        <span className={`font-bold ${theme.text}`}>{admin.count.toLocaleString()} actions</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLogsPage;
